// Team API Service
import { createApiClient } from '../utils/axiosConfig';
import { getApiBaseUrl } from '../utils/apiConfig';

const API_BASE_URL = getApiBaseUrl();

// Create axios instance with default config and auth error handling
// Note: teamApi uses /api prefix in baseURL
const apiClient = createApiClient(`${API_BASE_URL}/api`);

export interface TeamMember {
  _id: string;
  userId: string;
  memberId?: {
    _id: string;
    fullName?: string;
    email: string;
    company?: string;
    position?: string;
  };
  email: string; // Email of invited member
  role: 'PR Manager' | 'Content Writer' | 'Marketing Director' | 'Editor' | 'Viewer';
  permissions: string[];
  isActive: boolean;
  status: 'pending' | 'accepted' | 'rejected';
  invitationToken?: string;
  invitationExpires?: string;
  lastActive?: string;
  addedAt: string;
  acceptedAt?: string;
  addedBy: {
    _id: string;
    fullName?: string;
    email: string;
  };
}

export interface AddTeamMemberRequest {
  email: string;
  role?: 'PR Manager' | 'Content Writer' | 'Marketing Director' | 'Editor' | 'Viewer';
  permissions?: string[];
}

export interface UpdateTeamMemberRequest {
  role?: 'PR Manager' | 'Content Writer' | 'Marketing Director' | 'Editor' | 'Viewer';
  permissions?: string[];
  isActive?: boolean;
}

export const getTeamMembers = async (): Promise<TeamMember[]> => {
  try {
    const response = await apiClient.get('/team');
    return response.data.teamMembers || [];
  } catch (error: any) {
    console.error('Error fetching team members:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch team members');
  }
};

export const addTeamMember = async (data: AddTeamMemberRequest): Promise<TeamMember> => {
  try {
    const response = await apiClient.post('/team', data);
    return response.data.teamMember;
  } catch (error: any) {
    console.error('Error adding team member:', error);
    throw new Error(error.response?.data?.message || 'Failed to add team member');
  }
};

export const updateTeamMember = async (
  id: string,
  data: UpdateTeamMemberRequest
): Promise<TeamMember> => {
  try {
    const response = await apiClient.put(`/team/${id}`, data);
    return response.data.teamMember;
  } catch (error: any) {
    console.error('Error updating team member:', error);
    throw new Error(error.response?.data?.message || 'Failed to update team member');
  }
};

export const removeTeamMember = async (id: string): Promise<void> => {
  try {
    await apiClient.delete(`/team/${id}`);
  } catch (error: any) {
    console.error('Error removing team member:', error);
    throw new Error(error.response?.data?.message || 'Failed to remove team member');
  }
};

export const updateLastActive = async (): Promise<void> => {
  try {
    await apiClient.post('/team/update-active');
  } catch (error: any) {
    console.error('Error updating last active:', error);
    // Don't throw - this is a background operation
  }
};

export default {
  getTeamMembers,
  addTeamMember,
  updateTeamMember,
  removeTeamMember,
  updateLastActive
};

